import { useCallback, useEffect, useState } from "react";
import { Lock, RefreshCw, Download, Search, Package, Truck, Store, LogOut } from "lucide-react";
import { api } from "../lib/api";

const PIN_KEY = "jdm_admin_pin";

const fmtDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("en-NZ", {
      timeZone: "Pacific/Auckland",
      day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function AdminOrders() {
  const [pin, setPin] = useState(() => sessionStorage.getItem(PIN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(() => !!sessionStorage.getItem(PIN_KEY));
  const [pinInput, setPinInput] = useState("");
  const [error, setError] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState("all");
  const [search, setSearch] = useState("");

  const headers = useCallback(() => ({ "X-Admin-Pin": pin }), [pin]);

  const loadOrders = useCallback(async (r = range, q = search, p = pin) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/orders", {
        params: { range: r, q },
        headers: { "X-Admin-Pin": p },
      });
      setOrders(data.orders);
      setAuthed(true);
    } catch (e) {
      if (e.response?.status === 401) {
        setAuthed(false);
        sessionStorage.removeItem(PIN_KEY);
        setPin("");
      } else {
        setError("Could not load orders. Please try again.");
      }
    } finally {
      setLoading(false);
      setChecking(false);
    }
  }, [range, search, pin]);

  useEffect(() => {
    if (pin) loadOrders("all", "", pin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitPin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/orders/auth", { pin: pinInput });
      sessionStorage.setItem(PIN_KEY, pinInput);
      setPin(pinInput);
      setAuthed(true);
      loadOrders("all", "", pinInput);
    } catch (e) {
      setError(e.response?.status === 429 ? "Too many attempts — try again in 15 minutes." : "Incorrect PIN — please try again.");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(PIN_KEY);
    setPin("");
    setAuthed(false);
    setOrders([]);
  };

  const togglePacked = async (order) => {
    const next = !order.packed;
    setOrders((prev) => prev.map((o) => (o.session_id === order.session_id ? { ...o, packed: next } : o)));
    try {
      await api.post(`/admin/orders/${order.session_id}/packed`, { packed: next }, { headers: headers() });
    } catch {
      setOrders((prev) => prev.map((o) => (o.session_id === order.session_id ? { ...o, packed: !next } : o)));
    }
  };

  const downloadPdf = async (order) => {
    try {
      const res = await api.get(`/admin/orders/${order.session_id}/pdf`, {
        headers: headers(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `JustDessertMe_Order_${order.ref}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      setError("Could not download PDF.");
    }
  };

  const changeRange = (r) => {
    setRange(r);
    loadOrders(r, search);
  };

  if (checking) {
    return (
      <main data-testid="admin-orders-checking" className="max-w-md mx-auto px-6 py-24 text-center">
        <RefreshCw className="w-6 h-6 text-[#00b4ff] animate-spin mx-auto" />
      </main>
    );
  }

  if (!pin || !authed) {
    return (
      <main data-testid="admin-orders-login" className="max-w-md mx-auto px-6 py-24">
        <div className="glass rounded-3xl border border-white/10 p-8">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-10 h-10 rounded-full bg-[#ff1a8c]/15 text-[#ff1a8c] flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </span>
            <h1 className="font-script text-4xl text-white">Orders</h1>
          </div>
          <p className="text-sm text-gray-400 mb-5">Enter your admin PIN to view online orders.</p>
          <form onSubmit={submitPin} className="space-y-4">
            <input
              data-testid="admin-pin-input"
              type="password"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value)}
              placeholder="Admin PIN"
              autoFocus
              className="w-full bg-black/40 border border-white/15 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-[#ff1a8c] focus:outline-none"
            />
            {error && <p data-testid="admin-pin-error" className="text-sm text-[#ff1a8c]">{error}</p>}
            <button data-testid="admin-pin-submit" type="submit" className="neon-btn w-full">
              Unlock
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="admin-orders-page" className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-[0.65rem] uppercase tracking-[0.3em] text-[#00b4ff] mb-1">Admin</p>
          <h1 className="font-script text-5xl neon-gradient-text">Online Orders</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="admin-orders-refresh"
            onClick={() => loadOrders()}
            className="p-2.5 rounded-full border border-white/15 text-gray-300 hover:border-[#00b4ff] hover:text-[#00b4ff] transition-colors"
            aria-label="Refresh orders"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            data-testid="admin-orders-logout"
            onClick={logout}
            className="p-2.5 rounded-full border border-white/15 text-gray-300 hover:border-[#ff1a8c] hover:text-[#ff1a8c] transition-colors"
            aria-label="Lock dashboard"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-8">
        {[["all", "All"], ["week", "Last 7 days"], ["today", "Today"]].map(([val, label]) => (
          <button
            key={val}
            data-testid={`admin-filter-${val}`}
            onClick={() => changeRange(val)}
            className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] border transition-all ${
              range === val
                ? "border-[#ff1a8c] text-[#ff1a8c] bg-[#ff1a8c]/10"
                : "border-white/15 text-gray-400 hover:border-white/40"
            }`}
          >
            {label}
          </button>
        ))}
        <form
          onSubmit={(e) => { e.preventDefault(); loadOrders(range, search); }}
          className="flex items-center gap-2 ml-auto"
        >
          <input
            data-testid="admin-orders-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, email, ref…"
            className="bg-black/40 border border-white/15 rounded-full px-4 py-2 text-sm text-white placeholder-gray-500 focus:border-[#00b4ff] focus:outline-none w-56"
          />
          <button type="submit" aria-label="Search" data-testid="admin-orders-search-btn" className="p-2 rounded-full border border-white/15 text-gray-300 hover:border-[#00b4ff] hover:text-[#00b4ff]">
            <Search className="w-4 h-4" />
          </button>
        </form>
      </div>

      {orders.length === 0 && !loading && (
        <p data-testid="admin-orders-empty" className="text-gray-400 text-center py-16">No orders found for this view.</p>
      )}

      <div className="space-y-4">
        {orders.map((order) => {
          const isPickup = order.fulfillment?.type === "pickup";
          return (
            <div
              key={order.session_id}
              data-testid={`admin-order-${order.ref}`}
              className={`glass rounded-2xl border p-5 sm:p-6 transition-all ${
                order.packed ? "border-[#00b4ff]/40 opacity-70" : "border-white/10"
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-white font-semibold">{order.ref}</span>
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${
                      isPickup ? "border-[#00b4ff]/60 text-[#00b4ff]" : "border-[#ff1a8c]/60 text-[#ff1a8c]"
                    }`}>
                      {isPickup ? <Store className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                      {isPickup ? `Pickup${order.fulfillment?.pickup_day ? ` · ${order.fulfillment.pickup_day}` : ""}` : "Delivery"}
                    </span>
                    {order.packed && (
                      <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-[#00b4ff] text-[#00b4ff]">Packed</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">{fmtDate(order.created_at)}</p>
                  <p className="text-sm text-gray-300 mt-2">
                    {order.customer?.name} · {order.customer?.phone} · {order.customer?.email}
                  </p>
                  {!isPickup && order.customer?.address && (
                    <p className="text-xs text-gray-400 mt-0.5">{order.customer.address}</p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[#00b4ff] font-bold text-xl">${Number(order.amount).toFixed(2)}</p>
                  {order.voucher_code && (
                    <p className="text-[10px] uppercase tracking-[0.15em] text-gray-500">Voucher: {order.voucher_code}</p>
                  )}
                </div>
              </div>

              <ul className="mt-4 space-y-1 border-t border-white/10 pt-3">
                {order.item_lines.map((line, i) => (
                  <li key={i} className="flex justify-between gap-4 text-sm">
                    <span className="text-gray-300">{line.qty}× {line.label}</span>
                    <span className="text-gray-400 shrink-0">${line.line_total.toFixed(2)}</span>
                  </li>
                ))}
              </ul>

              {order.customer?.notes && (
                <p className="mt-3 text-sm italic text-gray-300 bg-white/5 border-l-2 border-[#ff1a8c] rounded px-3 py-2">
                  "{order.customer.notes}"
                </p>
              )}

              <div className="flex flex-wrap gap-3 mt-4">
                <button
                  data-testid={`admin-packed-toggle-${order.ref}`}
                  onClick={() => togglePacked(order)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] border transition-all ${
                    order.packed
                      ? "border-[#00b4ff] text-[#00b4ff] bg-[#00b4ff]/10"
                      : "border-white/20 text-gray-300 hover:border-[#00b4ff] hover:text-[#00b4ff]"
                  }`}
                >
                  <Package className="w-3.5 h-3.5" />
                  {order.packed ? "Packed ✓" : "Mark packed"}
                </button>
                <button
                  data-testid={`admin-pdf-download-${order.ref}`}
                  onClick={() => downloadPdf(order)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs uppercase tracking-[0.15em] border border-white/20 text-gray-300 hover:border-[#ff1a8c] hover:text-[#ff1a8c] transition-all"
                >
                  <Download className="w-3.5 h-3.5" />
                  Packing slip PDF
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {error && authed && <p className="text-sm text-[#ff1a8c] mt-6 text-center">{error}</p>}
    </main>
  );
}
